import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Globe, Lock, Edit2, Trash2, Upload, Loader2, Image, Package, Tag, Eye, Share2, Copy, Check, X, Search, Plus, Camera, Boxes, Wand2, Calendar } from 'lucide-react';
import { useAuth, getAuthHeaders } from '../context/AuthContext';
import Navigation from '../components/Navigation';
import FollowButton from '../components/FollowButton';
import ConfirmDialog from '../components/ConfirmDialog';
import BookmarkButton from '../components/BookmarkButton';
import { BACKEND_URL } from '../config/api';

// Default occasions - will be replaced by API data
const DEFAULT_OCCASIONS = [
  { value: "none", label: "None" },
  { value: "other", label: "Other" },
];

export default function DesignDetailPage({ fromCommunity = false }) {
  const { designId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  
  // Determine the source and tab from location state or referrer
  const source = location.state?.from || (fromCommunity ? 'explore' : 'create');
  const sourceTab = location.state?.tab || (fromCommunity ? 'latest' : 'designs');

  const goBack = () => {
    if (source === 'explore') {
      navigate(`/explore?tab=${sourceTab}`);
    } else if (source === 'create') {
      navigate(`/create?tab=${sourceTab}`);
    } else {
      navigate('/my-designs');
    }
  };
  
  const getBackLabel = () => {
    if (source === 'explore') {
      return 'Back to Explore';
    } else if (source === 'create') {
      return 'Back to Create';
    }
    return 'Back to My Designs';
  };

  const [design, setDesign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editOccasion, setEditOccasion] = useState('none');
  const [editTags, setEditTags] = useState([]);
  const [editTagInput, setEditTagInput] = useState('');
  const [editTagSuggestions, setEditTagSuggestions] = useState([]);
  const [showEditTagSuggestions, setShowEditTagSuggestions] = useState(false);
  const [editProducts, setEditProducts] = useState([]);
  const [editProductSearch, setEditProductSearch] = useState('');
  const [showEditProductDropdown, setShowEditProductDropdown] = useState(false);
  const [editManualProductInput, setEditManualProductInput] = useState('');
  const [stashItems, setStashItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [showImageDialog, setShowImageDialog] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const editTagInputRef = useRef(null);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [occasions, setOccasions] = useState(DEFAULT_OCCASIONS);
  const [occasionsLoaded, setOccasionsLoaded] = useState(false);

  // Fetch occasions from API
  useEffect(() => {
    const fetchOccasions = async () => {
      try {
        // Use the public filter-options endpoint
        const res = await fetch(`${BACKEND_URL}/api/designs/filter-options`, {
          credentials: 'include',
          headers: getAuthHeaders(),
        });
        if (res.ok) {
          const data = await res.json();
          const occasionValues = data.all_occasions || [];
          // Convert to {value, label} format and add "None" at start
          const occasionOptions = occasionValues.map(v => ({
            value: v,
            label: v.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
          }));
          setOccasions([{ value: "none", label: "None" }, ...occasionOptions]);
          setOccasionsLoaded(true);
        }
      } catch (err) {
        console.error('Failed to fetch occasions:', err);
        setOccasionsLoaded(true); // Still mark as loaded to not block UI
      }
    };
    fetchOccasions();
  }, []);

  useEffect(() => {
    const fetchDesign = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/designs/${designId}`, {
          credentials: 'include',
          headers: getAuthHeaders(),
        });
        if (res.ok) {
          const data = await res.json();
          setDesign(data);
          
          // Check bookmark status if not owner
          if (!data.is_owner) {
            const bookmarkRes = await fetch(`${BACKEND_URL}/api/bookmarks/check/${designId}`, {
              credentials: 'include',
              headers: getAuthHeaders(),
            });
            if (bookmarkRes.ok) {
              const bookmarkData = await bookmarkRes.json();
              setIsBookmarked(bookmarkData.bookmarked);
            }
          }
        } else {
          navigate('/my-designs', { replace: true });
        }
      } catch {
        navigate('/my-designs', { replace: true });
      } finally {
        setLoading(false);
      }
    };
    fetchDesign();
  }, [designId, navigate]);

  // Fetch stash items when entering edit mode
  useEffect(() => {
    if (!editing) return;
    const fetchStash = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/stash`, {
          credentials: 'include',
          headers: getAuthHeaders(),
        });
        if (res.ok) {
          const data = await res.json();
          setStashItems(Array.isArray(data) ? data : (data.items || []));
        }
      } catch { /* optional */ }
    };
    fetchStash();
  }, [editing]);

  // Fetch tag suggestions
  useEffect(() => {
    if (!editTagInput.trim()) { setEditTagSuggestions([]); return; }
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/designs/technique-tags?q=${encodeURIComponent(editTagInput)}`);
        if (res.ok) {
          const data = await res.json();
          setEditTagSuggestions((data.tags || []).filter(t => !editTags.includes(t)));
        }
      } catch { /* optional */ }
    }, 200);
    return () => clearTimeout(timer);
  }, [editTagInput, editTags]);

  // Cleanup camera on unmount
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
      }
    };
  }, []);

  // Auto-start edit mode if ?edit=true is in URL
  useEffect(() => {
    if (design && occasionsLoaded && searchParams.get('edit') === 'true' && !editing) {
      // Only auto-edit if user owns this design
      const isOwner = design.user_id === user?.user_id || design.creator?.user_id === user?.user_id;
      if (isOwner) {
        setEditTitle(design.title);
        setEditDescription(design.description || '');
        setEditOccasion(design.occasion || 'none');
        setEditTags([...(design.technique_tags || [])]);
        setEditProducts([...(design.products_used || [])]);
        setEditing(true);
        // Remove the edit param from URL to prevent re-triggering
        navigate(`${location.pathname}`, { replace: true, state: location.state });
      }
    }
  }, [design, occasionsLoaded, searchParams, user, editing, navigate, location]);

  const startEditing = () => {
    setEditTitle(design.title);
    setEditDescription(design.description || '');
    setEditOccasion(design.occasion || 'none');
    setEditTags([...(design.technique_tags || [])]);
    setEditProducts([...(design.products_used || [])]);
    setEditing(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/designs/${designId}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: editTitle,
          description: editDescription,
          occasion: editOccasion,
          technique_tags: editTags,
          products_used: editProducts,
        }),
      });
      if (res.ok) {
        setDesign((prev) => ({
          ...prev,
          title: editTitle,
          description: editDescription,
          occasion: editOccasion,
          technique_tags: editTags,
          products_used: editProducts,
        }));
        setEditing(false);
      }
    } catch (err) {
      console.error('Failed to save:', err);
    } finally {
      setSaving(false);
    }
  };

  // --- Image upload/snap dialog ---
  const openImageDialog = () => setShowImageDialog(true);
  const closeImageDialog = () => { setShowImageDialog(false); stopCamera(); };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;
    await uploadImageFile(file);
  };

  const uploadImageFile = async (file) => {
    setUploadingImage(true);
    closeImageDialog();
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${BACKEND_URL}/api/designs/${designId}/image`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders(),
        body: formData,
      });
      if (res.ok) {
        const data = await res.json();
        setDesign((prev) => ({ ...prev, image_url: data.image_url }));
      }
    } catch (err) {
      console.error('Failed to upload image:', err);
    } finally {
      setUploadingImage(false);
    }
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 960 } },
      });
      streamRef.current = stream;
      setShowCamera(true);
      setTimeout(() => {
        if (videoRef.current) videoRef.current.srcObject = stream;
      }, 100);
    } catch {
      alert('Camera access denied.');
    }
  };

  const capturePhoto = () => {
    if (!videoRef.current) return;
    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    canvas.getContext('2d').drawImage(videoRef.current, 0, 0);
    canvas.toBlob(async (blob) => {
      if (blob) {
        const file = new File([blob], 'capture.jpg', { type: 'image/jpeg' });
        await uploadImageFile(file);
      }
      stopCamera();
    }, 'image/jpeg', 0.9);
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    setShowCamera(false);
  };

  const handleDelete = async () => {
    try {
      await fetch(`${BACKEND_URL}/api/designs/${designId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: getAuthHeaders(),
      });
      navigate('/my-designs', { replace: true });
    } catch (err) {
      console.error('Failed to delete:', err);
    }
    setShowDeleteConfirm(false);
  };

  const handleTogglePublish = async () => {
    const endpoint = design.is_public ? 'unpublish' : 'publish';
    try {
      const res = await fetch(`${BACKEND_URL}/api/designs/${designId}/${endpoint}`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        setDesign((prev) => ({ ...prev, is_public: !prev.is_public }));
      } else {
        const data = await res.json();
        alert(data.detail || 'Failed');
      }
    } catch (err) {
      console.error('Failed:', err);
    }
  };

  // --- Edit: Tag helpers ---
  const addEditTag = (tag) => {
    const t = tag.toLowerCase().trim();
    if (t && !editTags.includes(t) && editTags.length < 10) {
      setEditTags(prev => [...prev, t]);
    }
    setEditTagInput('');
    setShowEditTagSuggestions(false);
    editTagInputRef.current?.focus();
  };

  const removeEditTag = (tag) => setEditTags(prev => prev.filter(t => t !== tag));

  const handleEditTagKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addEditTag(editTagInput);
    } else if (e.key === 'Backspace' && !editTagInput && editTags.length > 0) {
      removeEditTag(editTags[editTags.length - 1]);
    }
  };

  // --- Edit: Product helpers ---
  const removeEditProduct = (idx) => setEditProducts(prev => prev.filter((_, i) => i !== idx));

  const addEditStashProduct = (item) => {
    if (editProducts.length >= 20) return;
    const exists = editProducts.some(p => p.item_id === item.item_id || p.name?.toLowerCase() === item.name?.toLowerCase());
    if (!exists) {
      setEditProducts(prev => [...prev, {
        item_id: item.item_id,
        name: item.name,
        brand: item.brand || '',
        category: item.category || '',
        source: 'stash',
      }]);
    }
    setEditProductSearch('');
    setShowEditProductDropdown(false);
  };

  const addEditManualProduct = () => {
    const name = editManualProductInput.trim();
    if (!name || editProducts.length >= 20) return;
    if (!editProducts.some(p => p.name?.toLowerCase() === name.toLowerCase())) {
      setEditProducts(prev => [...prev, { name, source: 'manual' }]);
    }
    setEditManualProductInput('');
  };

  const filteredEditStash = stashItems.filter(item => {
    if (!editProductSearch.trim()) return true;
    const q = editProductSearch.toLowerCase();
    return (item.name || '').toLowerCase().includes(q) || (item.brand || '').toLowerCase().includes(q);
  }).filter(item => !editProducts.some(p => p.item_id === item.item_id || p.name?.toLowerCase() === item.name?.toLowerCase()));

  if (loading) {
    return (
      <div className="min-h-screen bg-cream">
        <Navigation />
        <div className="flex items-center justify-center py-24">
          <Loader2 className="animate-spin text-sage" size={32} />
        </div>
      </div>
    );
  }

  if (!design) return null;

  const isOwner = design.is_owner;

  const sourceBadge = (source) => {
    switch (source) {
      case 'stash':
        return <span className="shrink-0 px-1.5 py-0.5 bg-sage/15 text-sage text-[10px] rounded-full font-medium">Stash</span>;
      case 'ai_detected':
        return <span className="shrink-0 px-1.5 py-0.5 bg-rose/15 text-rose text-[10px] rounded-full font-medium">AI</span>;
      default:
        return <span className="shrink-0 px-1.5 py-0.5 bg-gold/15 text-gold-dark text-[10px] rounded-full font-medium">Manual</span>;
    }
  };

  return (
    <div className="min-h-screen bg-cream pb-20 lg:pb-0">
      <Navigation />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Back button */}
        <button
          onClick={goBack}
          className="flex items-center gap-1 text-sm text-gray-soft hover:text-charcoal mb-6 transition-colors"
          data-testid="back-to-designs"
        >
          <ArrowLeft size={16} />
          {getBackLabel()}
        </button>

        <div className="bg-white rounded-xl border border-beige overflow-hidden">
          {/* Image section */}
          <div className="relative aspect-video bg-cream">
            {design.image_url ? (
              <img src={design.image_url} alt={design.title} className="w-full h-full object-contain" />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center gap-3">
                <Image size={48} className="text-beige" />
                {isOwner && (
                  <button
                    onClick={openImageDialog}
                    className="px-4 py-2 bg-sage text-white rounded-lg text-sm hover:bg-sage-dark transition-colors flex items-center gap-2"
                    data-testid="upload-photo-btn"
                  >
                    <Upload size={14} />
                    Add Photo
                  </button>
                )}
              </div>
            )}
            {isOwner && design.image_url && (
              <button
                onClick={openImageDialog}
                className="absolute bottom-3 right-3 px-3 py-1.5 bg-charcoal/60 text-white rounded-lg text-xs hover:bg-charcoal/80 transition-colors flex items-center gap-1"
                data-testid="replace-image-btn"
              >
                <Upload size={12} />
                {uploadingImage ? 'Uploading...' : 'Replace'}
              </button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
            />
            {/* Status badge */}
            <div className={`absolute top-3 left-3 px-2.5 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${
              design.is_public ? 'bg-sage/90 text-white' : 'bg-charcoal/60 text-white'
            }`}>
              {design.is_public ? <Globe size={12} /> : <Lock size={12} />}
              {design.is_public ? 'Public' : 'Private'}
            </div>
            {design.view_count > 0 && (
              <div className="absolute top-3 right-3 px-2 py-1 bg-charcoal/40 text-white rounded-full text-xs flex items-center gap-1">
                <Eye size={12} />
                {design.view_count}
              </div>
            )}
          </div>

          {/* Image Upload/Snap Dialog */}
          {showImageDialog && (
            <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-charcoal/60 backdrop-blur-sm" onClick={closeImageDialog}>
              <div className="bg-white rounded-xl border border-beige shadow-xl max-w-sm w-full p-6" onClick={e => e.stopPropagation()} data-testid="image-upload-dialog">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-base font-medium text-charcoal">{design.image_url ? 'Replace Photo' : 'Add Photo'}</h3>
                  <button onClick={closeImageDialog} className="p-1 text-gray-soft hover:text-charcoal"><X size={18} /></button>
                </div>

                {showCamera ? (
                  <div className="relative aspect-video rounded-lg overflow-hidden bg-black mb-3">
                    <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" />
                    <div className="absolute bottom-3 inset-x-0 flex justify-center gap-3">
                      <button onClick={capturePhoto} className="w-14 h-14 rounded-full bg-white border-4 border-sage shadow-lg hover:scale-105 transition-transform" data-testid="capture-photo-btn" />
                      <button onClick={stopCamera} className="px-4 py-2 bg-charcoal/60 text-white rounded-lg text-sm hover:bg-charcoal/80">Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div
                    className="flex gap-3"
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault();
                      const file = e.dataTransfer.files?.[0];
                      if (file && file.type.startsWith('image/')) uploadImageFile(file);
                    }}
                  >
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="flex-1 aspect-[3/2] rounded-lg border-2 border-dashed border-beige hover:border-sage/50 bg-cream/50 flex flex-col items-center justify-center gap-2 transition-colors"
                      data-testid="dialog-upload-btn"
                    >
                      <Upload size={24} className="text-gray-soft" />
                      <span className="text-sm text-gray-soft">Upload or drop</span>
                    </button>
                    <button
                      onClick={startCamera}
                      className="flex-1 aspect-[3/2] rounded-lg border-2 border-dashed border-beige hover:border-sage/50 bg-cream/50 flex flex-col items-center justify-center gap-2 transition-colors"
                      data-testid="dialog-snap-btn"
                    >
                      <Camera size={24} className="text-gray-soft" />
                      <span className="text-sm text-gray-soft">Snap Photo</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Content */}
          <div className="p-6">
            {/* Title & Actions */}
            {editing ? (
              <div className="space-y-4 mb-4">
                  {/* Edit Title */}
                  <div>
                    <label className="block text-xs font-medium text-gray-soft mb-1">Title</label>
                    <input
                      type="text"
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      className="w-full text-lg font-medium text-charcoal border border-beige rounded-lg px-3 py-2 focus:outline-none focus:border-sage"
                      data-testid="edit-title-input"
                    />
                  </div>
                  {/* Edit Description */}
                  <div>
                    <label className="block text-xs font-medium text-gray-soft mb-1">Description</label>
                    <textarea
                      value={editDescription}
                      onChange={(e) => setEditDescription(e.target.value)}
                      className="w-full text-sm text-charcoal border border-beige rounded-lg p-3 focus:outline-none focus:border-sage resize-none"
                      rows={3}
                      data-testid="edit-description-input"
                    />
                  </div>
                  {/* Edit Occasion */}
                  <div>
                    <label className="block text-xs font-medium text-gray-soft mb-1 flex items-center gap-1">
                      <Calendar size={12} />
                      Occasion
                    </label>
                    <select
                      value={editOccasion}
                      onChange={(e) => setEditOccasion(e.target.value)}
                      className="w-full px-3 py-2 border border-beige rounded-lg text-sm text-charcoal focus:outline-none focus:border-sage bg-white"
                      data-testid="edit-occasion-select"
                    >
                      {occasions.map((occ) => (
                        <option key={occ.value} value={occ.value}>
                          {occ.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  {/* Edit Technique Tags */}
                  <div>
                    <label className="block text-xs font-medium text-gray-soft mb-1">
                      <Tag size={12} className="inline mr-1" />
                      Technique Tags
                    </label>
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {editTags.map(tag => (
                        <span key={tag} className="px-2 py-1 bg-sage/10 text-sage text-xs rounded-full flex items-center gap-1">
                          {tag}
                          <button onClick={() => removeEditTag(tag)} className="hover:text-red-500"><X size={12} /></button>
                        </span>
                      ))}
                    </div>
                    <div className="relative">
                      <input
                        ref={editTagInputRef}
                        type="text"
                        value={editTagInput}
                        onChange={(e) => { setEditTagInput(e.target.value); setShowEditTagSuggestions(true); }}
                        onKeyDown={handleEditTagKeyDown}
                        onFocus={() => setShowEditTagSuggestions(true)}
                        onBlur={() => setTimeout(() => setShowEditTagSuggestions(false), 200)}
                        placeholder={editTags.length < 10 ? 'Type a tag and press Enter' : 'Max 10 tags'}
                        disabled={editTags.length >= 10}
                        className="w-full px-3 py-2 border border-beige rounded-lg text-sm text-charcoal placeholder:text-gray-300 focus:outline-none focus:border-sage"
                        data-testid="edit-tag-input"
                      />
                      {showEditTagSuggestions && editTagSuggestions.length > 0 && (
                        <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-beige rounded-lg shadow-lg max-h-32 overflow-y-auto z-10">
                          {editTagSuggestions.map(s => (
                            <button key={s} onMouseDown={(e) => { e.preventDefault(); addEditTag(s); }} className="w-full text-left px-3 py-1.5 text-sm text-charcoal hover:bg-cream">
                              {s}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  {/* Edit Products */}
                  <div>
                    <label className="block text-xs font-medium text-gray-soft mb-1">
                      <Package size={12} className="inline mr-1" />
                      Products Used ({editProducts.length}/20)
                    </label>
                    {editProducts.length > 0 && (
                      <div className="space-y-1.5 mb-2">
                        {editProducts.map((p, idx) => (
                          <div key={`${p.name}-${idx}`} className="flex items-center gap-2 px-2.5 py-1.5 bg-cream/70 rounded-lg group min-w-0 overflow-hidden">
                            <span className="flex-1 text-sm text-charcoal truncate min-w-0">
                              {p.name}
                              {p.brand && <span className="text-gray-soft text-xs ml-1">by {p.brand}</span>}
                            </span>
                            {p.source && sourceBadge(p.source)}
                            <button onClick={() => removeEditProduct(idx)} className="shrink-0 p-0.5 text-gray-300 hover:text-red-500 transition-colors" data-testid={`edit-remove-product-${idx}`}>
                              <X size={14} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Search stash */}
                    <div className="relative mb-2">
                      <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300" />
                      <input
                        type="text"
                        value={editProductSearch}
                        onChange={(e) => { setEditProductSearch(e.target.value); setShowEditProductDropdown(true); }}
                        onFocus={() => setShowEditProductDropdown(true)}
                        onBlur={() => setTimeout(() => setShowEditProductDropdown(false), 200)}
                        placeholder="Search your stash to add..."
                        className="w-full pl-8 pr-3 py-2 border border-beige rounded-lg text-sm text-charcoal placeholder:text-gray-300 focus:outline-none focus:border-sage"
                        data-testid="edit-product-search"
                      />
                      {showEditProductDropdown && editProductSearch.trim() && (
                        <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-beige rounded-lg shadow-lg max-h-40 overflow-y-auto z-10">
                          {filteredEditStash.length === 0 ? (
                            <p className="px-3 py-2 text-xs text-gray-soft">No matching items</p>
                          ) : (
                            filteredEditStash.slice(0, 15).map(item => (
                              <button
                                key={item.item_id}
                                onMouseDown={(e) => { e.preventDefault(); addEditStashProduct(item); }}
                                className="w-full text-left px-3 py-2 text-sm flex items-center justify-between hover:bg-cream"
                              >
                                <div className="truncate">
                                  <span className="text-charcoal">{item.name}</span>
                                  {item.brand && <span className="text-gray-soft ml-1 text-xs">by {item.brand}</span>}
                                </div>
                                <Boxes size={12} className="text-sage shrink-0 ml-2" />
                              </button>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                    {/* Manual product input */}
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={editManualProductInput}
                        onChange={(e) => setEditManualProductInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addEditManualProduct(); } }}
                        placeholder="Or type a product name..."
                        className="flex-1 px-3 py-2 border border-beige rounded-lg text-sm text-charcoal placeholder:text-gray-300 focus:outline-none focus:border-sage"
                        data-testid="edit-manual-product-input"
                      />
                      <button
                        onClick={addEditManualProduct}
                        disabled={!editManualProductInput.trim() || editProducts.length >= 20}
                        className="px-3 py-2 border border-beige rounded-lg text-sm text-gray-soft hover:text-charcoal hover:border-sage disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        data-testid="edit-add-manual-product-btn"
                      >
                        <Plus size={16} />
                      </button>
                    </div>
                  </div>
                  {/* Save / Cancel */}
                  <div className="flex gap-2 pt-2">
                    <button
                      onClick={handleSave}
                      disabled={saving || !editTitle.trim()}
                      className="px-4 py-2 bg-sage text-white rounded-lg text-sm hover:bg-sage-dark disabled:opacity-50 flex items-center gap-2"
                      data-testid="save-edit-btn"
                    >
                      {saving ? <><Loader2 size={14} className="animate-spin" /> Saving...</> : 'Save Changes'}
                    </button>
                    <button
                      onClick={() => setEditing(false)}
                      className="px-4 py-2 border border-beige text-gray-soft rounded-lg text-sm hover:bg-cream"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="mb-4">
                  <div className="flex items-start justify-between gap-2">
                    <h1 className="text-xl font-medium text-charcoal flex-1 min-w-0" data-testid="design-title">{design.title}</h1>
                    {isOwner && (
                      <div className="flex items-center gap-1 shrink-0">
                        {design.is_public && (
                          <button
                            onClick={() => {
                              const url = `${window.location.origin}/d/${designId}`;
                              navigator.clipboard.writeText(url).then(() => {
                                setCopied(true);
                                setTimeout(() => setCopied(false), 2000);
                              });
                            }}
                            className="p-2 text-gray-soft hover:text-sage hover:bg-sage/5 rounded-lg transition-colors"
                            title="Copy share link"
                            data-testid="share-link-btn"
                          >
                            {copied ? <Check size={16} className="text-sage" /> : <Share2 size={16} />}
                          </button>
                        )}
                        <button
                          onClick={startEditing}
                          className="p-2 text-gray-soft hover:text-charcoal hover:bg-cream rounded-lg transition-colors"
                          title="Edit"
                          data-testid="edit-design-btn"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={handleTogglePublish}
                          className={`p-2 rounded-lg transition-colors ${
                            design.is_public
                              ? 'text-sage hover:text-sage-dark hover:bg-sage/5'
                              : 'text-gray-soft hover:text-charcoal hover:bg-cream'
                          }`}
                          title={design.is_public ? 'Make Private' : 'Make Public'}
                          data-testid="toggle-publish-btn"
                        >
                          {design.is_public ? <Globe size={16} /> : <Lock size={16} />}
                        </button>
                        <button
                          onClick={() => setShowDeleteConfirm(true)}
                          className="p-2 text-gray-soft hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete"
                          data-testid="delete-design-btn"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    )}
                  </div>
                  {design.description && (
                    <p className="text-sm text-gray-soft mt-2 whitespace-pre-wrap">{design.description}</p>
                  )}
                </div>
              )}

            {/* Creator info */}
            {design.creator && (
              <div className="flex items-center justify-between mb-4 pb-4 border-b border-beige">
                <div className="flex items-center gap-2">
                  {design.creator.picture ? (
                    <img src={design.creator.picture} alt={design.creator.name} className="w-8 h-8 rounded-full" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-sage-light flex items-center justify-center">
                      <span className="text-sage text-sm font-medium">{design.creator.name?.[0]}</span>
                    </div>
                  )}
                  <div>
                    <p className="text-sm font-medium text-charcoal">{design.creator.name}</p>
                    {design.creator.handle && (
                      <p className="text-xs text-sage">@{design.creator.handle}</p>
                    )}
                  </div>
                </div>
                {!isOwner && (
                  <div className="flex items-center gap-2">
                    <BookmarkButton 
                      designId={designId} 
                      initialBookmarked={isBookmarked}
                      size="default"
                      onToggle={setIsBookmarked}
                    />
                    <FollowButton targetUserId={design.creator.user_id} />
                  </div>
                )}
              </div>
            )}

            {/* Occasion (read-only) */}
            {!editing && design.occasion && design.occasion !== 'none' && (
              <div className="mb-4">
                <div className="flex items-center gap-1 text-xs text-gray-soft mb-2">
                  <Calendar size={12} />
                  Occasion
                </div>
                <span className="inline-flex px-2.5 py-1 bg-rose/10 text-rose-dark text-xs rounded-full capitalize">
                  {design.occasion}
                </span>
              </div>
            )}

            {/* Technique Tags (read-only) */}
            {!editing && design.technique_tags?.length > 0 && (
              <div className="mb-4">
                <div className="flex items-center gap-1 text-xs text-gray-soft mb-2">
                  <Tag size={12} />
                  Techniques
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {design.technique_tags.map((tag) => (
                    <span key={tag} className="inline-flex px-2.5 py-1 bg-sage/10 text-sage text-xs rounded-full">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Products Used (read-only) */}
            {!editing && design.products_used?.length > 0 && (
              <div>
                <div className="flex items-center gap-1 text-xs text-gray-soft mb-2">
                  <Package size={12} />
                  Products Used ({design.products_used.length})
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {design.products_used.map((p, i) => (
                    <span key={p.item_id || i} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-cream text-sm text-charcoal rounded-full">
                      {p.name}
                      {p.brand && <span className="text-xs text-gray-soft">by {p.brand}</span>}
                      {p.source && sourceBadge(p.source)}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title="Delete Design"
        message={`This will permanently remove "${design?.title || 'this design'}". This cannot be undone.`}
        cancelLabel="Cancel"
        confirmLabel="Delete"
        confirmVariant="danger"
        testId="delete-design-dialog"
      />
    </div>
  );
}
